import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI, followsAPI, qrAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, UserPlus, UserMinus, Users, Storefront, ShoppingCart, Star } from '@phosphor-icons/react';
import QRCodeCard from '../components/QRCodeCard';
import { toast } from 'sonner';
import { stripHtmlToText } from '../utils/text';

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function StorefrontPage() {
  const { username } = useParams();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const loadStorefront = useCallback(async () => {
    try {
      const res = await usersAPI.getStorefront(username);
      setStore(res.data);
      // Record QR scan (non-blocking)
      if (res.data.user?.user_id) {
        qrAPI.recordScan('profile', res.data.user.user_id).catch((err) => console.error('QR scan tracking failed:', err));
      }
      if (isAuthenticated && res.data.user?.user_id) {
        try {
          const followRes = await followsAPI.getFollowing();
          setIsFollowing(followRes.data.some(f => f.user_id === res.data.user.user_id));
        } catch (err) { console.error('Failed to load follow status:', err); }
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [username, isAuthenticated]);

  useEffect(() => { loadStorefront(); }, [loadStorefront]);

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Sign in to follow this vendor');
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followsAPI.unfollow(store.user.user_id);
        setIsFollowing(false);
        toast.success('Unfollowed');
      } else {
        await followsAPI.follow(store.user.user_id);
        setIsFollowing(true);
        toast.success('Now following!');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setFollowLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading storefront...</div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm max-w-md">
          <CardContent className="p-8 text-center">
            <Storefront className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
            <p className="text-white text-lg font-bold mb-2">Store Not Found</p>
            <p className="text-[#999999] mb-6">No user found with username ${username}</p>
            <Link to="/explore">
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Browse Marketplace</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const storeUser = store.user;
  const displayName = storeUser?.full_name || storeUser?.name || 'Unknown User';
  const products = store.products || [];
  const isOwnStore = currentUser?.user_id === storeUser?.user_id;
  const themeColor = storeUser?.theme || '#22C55E';

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={DARK_LOGO} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <nav className="flex items-center gap-2">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="text-white rounded-sm" style={{ backgroundColor: themeColor }} data-testid="dashboard-button">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="text-white rounded-sm" style={{ backgroundColor: themeColor }} data-testid="login-button">
                  Sign in
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Store Hero */}
      <div className="border-b border-[#333333]" style={{ background: `linear-gradient(to bottom, ${themeColor}15, #111111)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12 text-center">
          {storeUser.avatar_url ? (
            <img src={storeUser.avatar_url} alt={displayName} className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-4 object-cover border-4" style={{ borderColor: themeColor }} data-testid="store-avatar" />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl sm:text-3xl font-bold" style={{ backgroundColor: `${themeColor}20`, color: themeColor }} data-testid="store-avatar">
              {displayName?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="store-name">
            {displayName}
          </h1>
          <p className="font-semibold text-base sm:text-lg mb-2" style={{ color: themeColor }} data-testid="store-username">
            ${storeUser.username}
          </p>
          {storeUser.bio && (
            <p className="text-[#999999] max-w-md mx-auto mb-4 text-sm sm:text-base" data-testid="store-bio">{storeUser.bio}</p>
          )}
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex items-center gap-1 text-[#999999]">
              <Users className="w-4 h-4" />
              <span className="text-sm">{store.follower_count || 0} followers</span>
            </div>
            <div className="flex items-center gap-1 text-[#999999]">
              <Storefront className="w-4 h-4" />
              <span className="text-sm">{products.length} products</span>
            </div>
          </div>
          {/* Seller Average Rating */}
          {store.avg_rating > 0 && (
            <div className="flex items-center justify-center gap-1 mb-4" data-testid="store-rating">
              <Star className="w-4 h-4" weight="fill" style={{ color: '#F59E0B' }} />
              <span className="text-sm text-white font-medium">{store.avg_rating}</span>
              <span className="text-sm text-[#999999]">({store.total_reviews} reviews)</span>
            </div>
          )}
          {!isOwnStore && (
            <Button
              onClick={handleFollow}
              disabled={followLoading}
              variant={isFollowing ? 'outline' : 'default'}
              className={isFollowing
                ? 'rounded-sm border-[#333333] hover:bg-[#333333]'
                : 'rounded-sm text-white'
              }
              style={isFollowing ? { color: themeColor } : { backgroundColor: themeColor }}
              data-testid="follow-button"
            >
              {isFollowing ? <><UserMinus className="w-4 h-4 mr-2" /> Following</> : <><UserPlus className="w-4 h-4 mr-2" /> Follow</>}
            </Button>
          )}

          {/* Store QR Code */}
          <div className="mt-6 max-w-xs mx-auto">
            <QRCodeCard
              type="profile"
              targetId={storeUser.username || storeUser.user_id}
              title={`${displayName}'s Store`}
              subtitle={`divispend.com/store/${storeUser.username}`}
              showStats={isOwnStore}
            />
          </div>
        </div>
      </div>

      {/* Products */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h2 className="text-xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
          <ShoppingCart className="w-5 h-5 inline-block mr-2" style={{ color: themeColor }} weight="duotone" />
          Products & Services
        </h2>
        {products.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm">
            <CardContent className="p-12 text-center">
              <Storefront className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
              <p className="text-[#999999]">No products listed yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <Card key={product.product_id} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none hover:-translate-y-1 hover:border-[#22C55E] transition-all duration-200 overflow-hidden" data-testid={`storefront-product-${product.product_id}`}>
                {product.image_url ? (
                  <div className="aspect-video bg-[#111111] overflow-hidden">
                    <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }} />
                  </div>
                ) : (
                  <div className="aspect-video bg-[#1A1A1A] flex items-center justify-center">
                    <Storefront className="w-12 h-12 text-[#333333]" />
                  </div>
                )}
                <CardContent className="p-4">
                  <span className="text-xs bg-[#333333] px-2 py-1 rounded-sm text-[#999999] inline-block mb-2">{product.category}</span>
                  <h3 className="font-bold text-white mb-1 line-clamp-1" style={{ fontFamily: 'Cabinet Grotesk' }}>{product.name}</h3>
                  <p className="text-sm text-[#999999] mb-3 line-clamp-2">{stripHtmlToText(product.description)}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>{formatCurrency(product.price)}</p>
                    <Link to={`/product/${product.product_id}`}>
                      <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-xs" data-testid={`buy-${product.product_id}`}>
                        View <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
